import createClient from 'openapi-fetch';
import type { components, paths } from '../api/schema';
import { lingua, t } from './lingua';

/**
 * Il client dell'hub. I tipi vengono da `src/api/schema.d.ts`, generato dallo
 * spec OpenAPI con `just openapi-client`: cambiare un handler in Go e non
 * rigenerare rompe la compilazione, che è il punto.
 *
 * `baseUrl` è vuoto perché il prefisso `/api/v1` sta già nei path dello spec.
 * In sviluppo il proxy di Vite li inoltra all'hub mantenendo l'origine, che è
 * ciò che la protezione CSRF dell'hub pretende.
 */
export const api = createClient<paths>({ baseUrl: '' });

// La lingua scelta dalla persona, su ogni chiamata: l'hub risponde in quella,
// messaggi d'errore compresi. Quella del browser qui non conta, perché chi ha
// il browser in inglese può aver scelto l'italiano.
api.use({
  onRequest({ request }) {
    request.headers.set('Accept-Language', lingua());
    return request;
  },
});

export type Server = components['schemas']['Server'];
/**
 * La risposta alla creazione, e l'unica che porta il comando di bootstrap: il
 * token in chiaro esiste solo in quel momento. È un tipo a sé anche nell'API —
 * l'elenco dei server non lo promette nemmeno nello schema.
 */
export type ServerCreated = components['schemas']['ServerCreated'];
export type Job = components['schemas']['Job'];
export type Instance = components['schemas']['Instance'];
export type Domain = components['schemas']['Domain'];
export type BackupDestination = components['schemas']['Destination'];
export type BackupCoverage = components['schemas']['Coverage'];
export type Backup = components['schemas']['Backup'];
export type Organization = components['schemas']['Organization'];
export type CloudCredential = components['schemas']['Credential'];
export type { components };
export type ServerStatus = Server['status'];

/**
 * L'errore che l'hub restituisce, in forma leggibile. huma parla RFC 7807:
 * `title` è la categoria, `detail` il motivo. Mostrare `detail` quando c'è
 * evita il generico "errore" davanti a un messaggio che l'hub ha già scritto.
 */
export class ApiError extends Error {
  readonly status: number;
  constructor(status: number, body: unknown) {
    const p = body as { title?: string; detail?: string } | undefined;
    super(p?.detail || p?.title || t('errore {status}', { status }));
    this.name = 'ApiError';
    this.status = status;
  }
}

type Result<T> = { data?: T; error?: unknown; response: Response };

/**
 * Attende la risposta e ne estrae il corpo, o lancia. openapi-fetch restituisce
 * `{ data, error }` invece di lanciare; TanStack Query si aspetta l'opposto, e
 * questa è la sola riga in cui le due convenzioni si incontrano.
 */
export async function must<T>(p: Promise<Result<T>>): Promise<T> {
  const r = await p;
  if (r.error !== undefined) throw new ApiError(r.response.status, r.error);
  if (r.data === undefined) throw new ApiError(r.response.status, undefined);
  return r.data;
}

/** Per le operazioni che non restituiscono un corpo (204). */
export async function mustOk(p: Promise<Result<unknown>>): Promise<void> {
  const r = await p;
  if (r.error !== undefined) throw new ApiError(r.response.status, r.error);
}
